/**
 * ChatBot — Assistente Virtual reutilizável
 *
 * Props:
 *   endpoint       string  URL da Netlify Function (default: /.netlify/functions/chat)
 *   greeting       string  Mensagem inicial do bot
 *   accentColor    string  Cor de destaque (default: #F47E2F)
 *   logoSrc        string  Logo exibido no header do chat
 *   title          string  Título do widget
 *   maxMessages    number  Limite de mensagens por sessão (default: 20)
 */

import React, { useState, useEffect, useRef } from 'react'
import styles from './ChatBot.module.css'

// Parses **bold** markdown and newlines into React nodes
function renderText(text) {
  const parts = []
  const re = /\*\*([\s\S]*?)\*\*/g
  let last = 0
  let m
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ bold: false, content: text.slice(last, m.index) })
    parts.push({ bold: true, content: m[1] })
    last = re.lastIndex
  }
  if (last < text.length) parts.push({ bold: false, content: text.slice(last) })

  return parts.map((seg, i) => {
    if (seg.bold) return <strong key={i}>{seg.content}</strong>
    return seg.content.split('\n').map((line, j, arr) => (
      <React.Fragment key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </React.Fragment>
    ))
  })
}

const EMAIL_MARKER_RE = /\[\[SEND_EMAIL:([\s\S]*?)\]\]/

async function sendCommercialEmail(data) {
  try {
    await fetch('/.netlify/functions/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  } catch {
    // Silent fail — the bot's text already told the user what happened
  }
}

const DEFAULT_GREETING =
  'Olá! Sou o Assistente Virtual da EQPI Tech. Posso responder dúvidas sobre nossos produtos e serviços de gestão de fornecedores. Como posso ajudar?'

const SESSION_KEY = 'eqpi_chat_count'
const SESSION_MESSAGES_KEY = 'eqpi_chat_messages'

export default function ChatBot({
  endpoint = '/.netlify/functions/chat',
  greeting = DEFAULT_GREETING,
  accentColor = '#F47E2F',
  logoSrc = '/logos/Logo_EQPI.svg',
  title = 'Assistente EQPI',
  fabLabel = 'Fale com o Assistente',
  lang = 'pt',
  maxMessages = 20,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sessionCount, setSessionCount] = useState(0)
  const [limitReached, setLimitReached] = useState(false)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)

  // Restore session on mount
  useEffect(() => {
    const count = parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10)
    setSessionCount(count)
    if (count >= maxMessages) setLimitReached(true)

    const saved = sessionStorage.getItem(SESSION_MESSAGES_KEY)
    if (saved) {
      try { setMessages(JSON.parse(saved)) } catch { /* ignore */ }
    } else {
      setMessages([{ role: 'assistant', content: greeting }])
    }
  }, [greeting, maxMessages])

  // Persist messages
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(messages))
    }
  }, [messages])

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 200)
  }, [isOpen])

  async function sendMessage() {
    const text = input.trim()
    if (!text || isLoading || limitReached) return

    const newCount = sessionCount + 1
    setSessionCount(newCount)
    sessionStorage.setItem(SESSION_KEY, String(newCount))
    if (newCount >= maxMessages) setLimitReached(true)

    const userMsg = { role: 'user', content: text }
    const updated = [...messages, userMsg]
    setMessages(updated)
    setInput('')
    setIsLoading(true)

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updated.filter((m) => m.role === 'user' || m.role === 'assistant'),
          lang,
        }),
      })

      const data = await res.json()
      const rawReply = res.ok
        ? (data.reply || 'Não entendi. Pode reformular?')
        : (data.error || 'Ocorreu um erro. Tente novamente.')

      const markerMatch = rawReply.match(EMAIL_MARKER_RE)
      const reply = markerMatch ? rawReply.replace(EMAIL_MARKER_RE, '').trim() : rawReply

      if (markerMatch) {
        try {
          const emailData = JSON.parse(markerMatch[1])
          sendCommercialEmail(emailData)
        } catch { /* malformed JSON — ignore */ }
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: reply }])
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sem conexão no momento. Tente novamente em instantes.' },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  function resetSession() {
    sessionStorage.removeItem(SESSION_KEY)
    sessionStorage.removeItem(SESSION_MESSAGES_KEY)
    setSessionCount(0)
    setLimitReached(false)
    setMessages([{ role: 'assistant', content: greeting }])
  }

  const remaining = maxMessages - sessionCount

  return (
    <>
      {/* Floating button */}
      <button
        className={`${styles.fab} ${isOpen ? styles.fabHidden : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Abrir assistente virtual"
        style={{ '--accent': accentColor }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <span>{fabLabel}</span>
      </button>

      {/* Chat window */}
      <div
        className={`${styles.window} ${isOpen ? styles.windowOpen : ''}`}
        style={{ '--accent': accentColor }}
        role="dialog"
        aria-label="Assistente virtual"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {logoSrc && <img src={logoSrc} alt="" className={styles.headerLogo} aria-hidden="true" />}
            <div>
              <p className={styles.headerTitle}>{title}</p>
              <span className={styles.headerStatus}>
                <span className={styles.statusDot} />
                Online
              </span>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.iconBtn} onClick={resetSession} title="Nova conversa" aria-label="Nova conversa">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
            </button>
            <button className={styles.iconBtn} onClick={() => setIsOpen(false)} aria-label="Fechar assistente">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className={styles.messages}>
          {messages.map((msg, i) => (
            <div key={i} className={`${styles.bubble} ${msg.role === 'user' ? styles.bubbleUser : styles.bubbleBot}`}>
              {renderText(msg.content)}
            </div>
          ))}

          {isLoading && (
            <div className={`${styles.bubble} ${styles.bubbleBot} ${styles.bubbleTyping}`}>
              <span /><span /><span />
            </div>
          )}

          {limitReached && (
            <div className={styles.limitBanner}>
              Limite da sessão atingido. Para continuar, fale diretamente com nossa equipe:
              <a href="mailto:comercial@equipoinfo.com.br">comercial@equipoinfo.com.br</a>
              <button className={styles.resetBtn} onClick={resetSession}>Nova conversa</button>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className={styles.inputArea}>
          {!limitReached && remaining <= 5 && (
            <p className={styles.countWarning}>{remaining} mensagen{remaining !== 1 ? 's' : ''} restante{remaining !== 1 ? 's' : ''} nesta sessão</p>
          )}
          <div className={styles.inputRow}>
            <textarea
              ref={inputRef}
              className={styles.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={limitReached ? 'Limite atingido' : 'Digite sua mensagem…'}
              disabled={limitReached || isLoading}
              rows={1}
              maxLength={500}
            />
            <button
              className={styles.sendBtn}
              onClick={sendMessage}
              disabled={!input.trim() || isLoading || limitReached}
              aria-label="Enviar mensagem"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m22 2-7 20-4-9-9-4 20-7z"/>
              </svg>
            </button>
          </div>
          <p className={styles.footer}>Respostas geradas por IA · Verifique informações importantes</p>
        </div>
      </div>
    </>
  )
}
